import React, { useEffect, useRef, useState } from "react";
import { Modal } from "react-bootstrap";
import toast from "react-hot-toast";
import ApiService from "../../../../services/ApiService";
import { PUBLIC_EVENTS_HOST } from "./dynamicEventDesigns";
import { TRANSLATABLE_FIELD_LABELS } from "./dynamicEventTranslatable";

// Fallback list if the backend languages endpoint is unavailable.
const DEFAULT_LANGUAGES = [
  { code: "fr", label: "Français", english: "French" },
  { code: "de", label: "Deutsch", english: "German" },
  { code: "es", label: "Español", english: "Spanish" },
  { code: "it", label: "Italiano", english: "Italian" },
  { code: "pt", label: "Português", english: "Portuguese" },
  { code: "nl", label: "Nederlands", english: "Dutch" },
  { code: "ja", label: "日本語", english: "Japanese" },
  { code: "zh", label: "中文", english: "Chinese" },
  { code: "ko", label: "한국어", english: "Korean" },
  { code: "ar", label: "العربية", english: "Arabic" },
];

const formatDate = (ts) => {
  if (!ts) return "";
  try {
    return new Date(ts * (ts < 1e12 ? 1000 : 1)).toLocaleString(undefined, {
      dateStyle: "medium",
      timeStyle: "short",
    });
  } catch (error) {
    return "";
  }
};

/**
 * "Languages" section of the event editor. Lists generated language versions
 * with their status, lets staff generate / regenerate / delete them and
 * copy the public ?lang= URL.
 *
 * Props:
 *   employee, eventId, pageUrl
 *   translations           -> current list from the saved event
 *   onTranslationsChange(list)
 *   contentChangedSinceLoad -> true when translatable fields were edited but
 *                              not yet saved (shows the "regenerate" warning)
 *   hasUnsavedChanges      -> any unsaved form changes (or never saved)
 *   onSaveChanges({ lang }) -> saves the form in place; resolves to
 *                              { ok, switched } (switched = a new event was
 *                              created and the form re-mounted in edit mode)
 *   autoGenerateLang / onAutoGenerateHandled -> continue a "save & generate"
 *                              after the form re-mounted for a new event
 *   onPreviewLang(lang)    -> optional: switch the preview to this language
 */
export default function DynamicEventTranslations({
  employee,
  eventId,
  pageUrl,
  translations = [],
  onTranslationsChange,
  contentChangedSinceLoad = false,
  hasUnsavedChanges = false,
  onSaveChanges,
  autoGenerateLang = "",
  onAutoGenerateHandled,
  onPreviewLang,
}) {
  const [languages, setLanguages] = useState(DEFAULT_LANGUAGES);
  const [selectedLang, setSelectedLang] = useState("fr");
  const [customCode, setCustomCode] = useState("");
  const [busyLang, setBusyLang] = useState(""); // lang currently generating / deleting
  const [confirmDelete, setConfirmDelete] = useState("");
  const [confirmGenerateLang, setConfirmGenerateLang] = useState(""); // save-first modal
  const [savingBeforeGenerate, setSavingBeforeGenerate] = useState(false);
  const autoGenerateStarted = useRef(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const api = new ApiService({ employee });
        const response = await api.get("landmark/dynamicevents/getSupportedLanguages");
        if (!cancelled && response && response.success && Array.isArray(response.languages)) {
          setLanguages(response.languages);
        }
      } catch (error) {
        // keep defaults
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [employee]);

  const existingCodes = new Set((translations || []).map((t) => t.lang));
  const langToGenerate =
    selectedLang === "other"
      ? customCode.toLowerCase().trim().replace(/[^a-z-]/g, "")
      : selectedLang;
  const labelFor = (code) =>
    languages.find((l) => l.code === code)?.label || code.toUpperCase();

  // Entry point for every Generate / Regenerate button. If the form has
  // unsaved changes (or the event was never saved) ask first, because the
  // translation is written from the *saved* English content.
  const generate = (lang) => {
    if (!lang || lang === "en") {
      toast.error("Please choose a language");
      return;
    }
    if (busyLang) return;
    if (!eventId || hasUnsavedChanges) {
      setConfirmGenerateLang(lang);
      return;
    }
    runGenerate(lang);
  };

  const saveAndGenerate = async () => {
    const lang = confirmGenerateLang;
    if (!lang || !onSaveChanges) return;
    setSavingBeforeGenerate(true);
    try {
      const result = await onSaveChanges({ lang });
      if (!result || !result.ok) {
        return; // save failed - its toast explains why; keep the modal open
      }
      setConfirmGenerateLang("");
      if (result.switched) {
        // a new event was created and the form re-mounted in edit mode; that
        // instance continues via autoGenerateLang
        return;
      }
      runGenerate(lang);
    } finally {
      setSavingBeforeGenerate(false);
    }
  };

  const generateWithoutSaving = () => {
    const lang = confirmGenerateLang;
    setConfirmGenerateLang("");
    if (eventId) runGenerate(lang);
  };

  // Continue a "save & generate" after a brand-new event re-mounted the form.
  useEffect(() => {
    if (!autoGenerateLang || !eventId || autoGenerateStarted.current) return;
    autoGenerateStarted.current = true;
    onAutoGenerateHandled && onAutoGenerateHandled();
    runGenerate(autoGenerateLang);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoGenerateLang, eventId]);

  const runGenerate = async (lang) => {
    if (!eventId) {
      toast.error("Save the event first, then generate languages");
      return;
    }
    if (busyLang) return;
    setBusyLang(lang);
    const toastId = toast.loading(
      `Writing the ${labelFor(lang)} version… this usually takes 30–90 seconds`
    );
    try {
      const api = new ApiService({ employee });
      const response = await api.post(
        "landmark/dynamicevents/ai/translateEvent",
        { eventId, lang, label: labelFor(lang) },
        undefined,
        { timeout: 180000 }
      );
      if (response && response.success && response.translation) {
        const next = (translations || []).filter((t) => t.lang !== lang);
        next.push(response.translation);
        onTranslationsChange && onTranslationsChange(next);
        toast.success(`${labelFor(lang)} version ready`, { id: toastId });
        if (Array.isArray(response.warnings) && response.warnings.length) {
          console.warn("translateEvent warnings", response.warnings);
        }
      } else {
        throw new Error(response?.error || "Generation failed");
      }
    } catch (error) {
      console.error("DynamicEventTranslations.generate()", error);
      toast.error(error?.message || "Could not generate translation", { id: toastId });
    } finally {
      setBusyLang("");
    }
  };

  const remove = async (lang) => {
    if (busyLang) return;
    setBusyLang(lang);
    try {
      const api = new ApiService({ employee });
      const response = await api.post("landmark/dynamicevents/deleteTranslation", {
        eventId,
        lang,
      });
      if (response && response.success) {
        onTranslationsChange &&
          onTranslationsChange((translations || []).filter((t) => t.lang !== lang));
        toast.success(`${labelFor(lang)} version removed`);
      } else {
        throw new Error(response?.error || "Delete failed");
      }
    } catch (error) {
      toast.error(error?.message || "Could not delete translation");
    } finally {
      setBusyLang("");
      setConfirmDelete("");
    }
  };

  const copy = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success("Link copied");
    } catch (error) {
      toast.error("Could not copy");
    }
  };

  const publicUrl = (lang) => `${PUBLIC_EVENTS_HOST}/${pageUrl}?lang=${lang}`;
  const staleCount = (translations || []).filter((t) => t.stale).length;

  return (
    <div className="event-translations">
      <h4 className="event-translations-title">
        Languages
        {translations.length ? (
          <span className="event-translations-count">{translations.length + 1}</span>
        ) : null}
      </h4>
      <div className="example-text">
        Generate a version of this page in another language. The AI rewrites the copy the way a
        native speaker would (names, venues, addresses, emails and times are kept as they are) and
        it is served at <code>?lang=xx</code>. The English page is always the source: edit English,
        then regenerate.
      </div>

      {!eventId ? (
        <div className="event-translations-note">
          <i className="fa-solid fa-circle-info" /> This event hasn't been saved yet. You can
          still pick a language below — you'll be asked to save first.
        </div>
      ) : null}
      {contentChangedSinceLoad && translations.length ? (
            <div className="event-translations-warning">
              <i className="fa-solid fa-triangle-exclamation" /> You have changed {" "}
              {TRANSLATABLE_FIELD_LABELS.split(",")[0]} or other translatable content. When you
              save, the existing language version{translations.length > 1 ? "s" : ""} will be
              marked <b>out of date</b> — regenerate {translations.length > 1 ? "them" : "it"}{" "}
              afterwards so visitors don't see old copy.
            </div>
          ) : staleCount ? (
            <div className="event-translations-warning">
              <i className="fa-solid fa-triangle-exclamation" /> {staleCount} language version
              {staleCount > 1 ? "s are" : " is"} out of date with the English content. Regenerate
              to update.
            </div>
          ) : null}

          {translations.length ? (
            <div className="event-translations-list">
              {translations.map((t) => (
                <div
                  key={t.lang}
                  className={`event-translations-row ${t.stale ? "is-stale" : ""}`}
                >
                  <div className="event-translations-main">
                    <div className="event-translations-name">
                      <b>{t.label || t.lang.toUpperCase()}</b>{" "}
                      <span className="event-translations-code">{t.lang}</span>
                      {t.stale ? (
                        <span className="badge-stale">
                          <i className="fa-solid fa-triangle-exclamation" /> Out of date — regenerate
                        </span>
                      ) : (
                        <span className="badge-fresh">
                          <i className="fa-solid fa-check" /> Up to date
                        </span>
                      )}
                    </div>
                    <div className="event-translations-meta">
                      Generated {formatDate(t.generatedTime)}
                      {t.generatedBy ? ` by ${t.generatedBy}` : ""}
                    </div>
                    <div className="event-translations-link">
                      <a
                        href={publicUrl(t.lang)}
                        target="_blank"
                        rel="noreferrer"
                        title={publicUrl(t.lang)}
                      >
                        {publicUrl(t.lang)}
                      </a>
                      <button
                        type="button"
                        className="icon-btn"
                        title="Copy link"
                        onClick={() => copy(publicUrl(t.lang))}
                      >
                        <i className="fa-regular fa-copy" />
                      </button>
                    </div>
                  </div>
                  <div className="event-translations-actions">
                    {onPreviewLang ? (
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-secondary"
                        onClick={() => onPreviewLang(t.lang)}
                      >
                        <i className="fa-solid fa-eye" /> Preview
                      </button>
                    ) : null}
                    <button
                      type="button"
                      className={`btn btn-sm ${t.stale ? "btn-primary" : "btn-outline-primary"}`}
                      disabled={!!busyLang}
                      onClick={() => generate(t.lang)}
                    >
                      {busyLang === t.lang ? (
                        <>
                          <i className="fa-solid fa-spinner fa-spin" /> Generating…
                        </>
                      ) : (
                        <>
                          <i className="fa-solid fa-rotate" /> Regenerate
                        </>
                      )}
                    </button>
                    {confirmDelete === t.lang ? (
                      <>
                        <button
                          type="button"
                          className="btn btn-sm btn-danger"
                          disabled={!!busyLang}
                          onClick={() => remove(t.lang)}
                        >
                          Confirm delete
                        </button>
                        <button
                          type="button"
                          className="btn btn-sm btn-link"
                          onClick={() => setConfirmDelete("")}
                        >
                          Cancel
                        </button>
                      </>
                    ) : (
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-danger"
                        disabled={!!busyLang}
                        onClick={() => setConfirmDelete(t.lang)}
                        title="Delete this language version"
                      >
                        <i className="fa-solid fa-trash" />
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : null}

          <div className="event-translations-add">
            <label htmlFor="translationLang">Add a language</label>
            <select
              id="translationLang"
              className="form-select"
              value={selectedLang}
              onChange={(e) => setSelectedLang(e.target.value)}
              disabled={!!busyLang}
            >
              {languages.map((l) => (
                <option key={l.code} value={l.code} disabled={existingCodes.has(l.code)}>
                  {l.label} ({l.english}){existingCodes.has(l.code) ? " — generated" : ""}
                </option>
              ))}
              <option value="other">Other (enter code)…</option>
            </select>
            {selectedLang === "other" ? (
              <input
                type="text"
                className="form-control"
                placeholder="ISO code, e.g. sv"
                maxLength={8}
                value={customCode}
                onChange={(e) => setCustomCode(e.target.value)}
              />
            ) : null}
            <button
              type="button"
              className="btn btn-primary"
              disabled={!!busyLang || !langToGenerate || existingCodes.has(langToGenerate)}
              onClick={() => generate(langToGenerate)}
            >
              {busyLang && busyLang === langToGenerate ? (
                <>
                  <i className="fa-solid fa-spinner fa-spin" /> Generating…
                </>
              ) : (
                <>
                  <i className="fa-solid fa-wand-magic-sparkles" /> Generate{" "}
                  {langToGenerate ? labelFor(langToGenerate) : ""} version
                </>
              )}
            </button>
            <span className="example-text">Takes about 30–90 seconds.</span>
          </div>

      <Modal
        show={!!confirmGenerateLang}
        onHide={() => (savingBeforeGenerate ? null : setConfirmGenerateLang(""))}
        centered
        className="save-before-generate-modal"
      >
        <Modal.Header closeButton={!savingBeforeGenerate}>
          <Modal.Title>Save changes before generating?</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {eventId ? (
            <>
              <p>
                You have unsaved changes. The {labelFor(confirmGenerateLang)} version is written
                from the <b>saved</b> English content, so anything you haven't saved yet would be
                left out and the translation would be out of date straight away.
              </p>
              <p className="mb-0">Save your changes first, then generate?</p>
            </>
          ) : (
            <p className="mb-0">
              This event hasn't been saved yet. Save it now, then generate the{" "}
              {labelFor(confirmGenerateLang)} version from the saved content?
            </p>
          )}
        </Modal.Body>
        <Modal.Footer>
          <button
            type="button"
            className="btn btn-link"
            disabled={savingBeforeGenerate}
            onClick={() => setConfirmGenerateLang("")}
          >
            Cancel
          </button>
          {eventId ? (
            <button
              type="button"
              className="btn btn-outline-secondary"
              disabled={savingBeforeGenerate}
              onClick={generateWithoutSaving}
            >
              Generate without saving
            </button>
          ) : null}
          <button
            type="button"
            className="btn btn-primary"
            disabled={savingBeforeGenerate || !onSaveChanges}
            onClick={saveAndGenerate}
          >
            {savingBeforeGenerate ? (
              <>
                <i className="fa-solid fa-spinner fa-spin" /> Saving…
              </>
            ) : (
              <>
                <i className="fa-solid fa-floppy-disk" /> Save & generate
              </>
            )}
          </button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}
